import PropTypes from 'prop-types';
import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import { SigStatusChangeCopy } from 'enums/SigManagement.enum';

const countPhrase = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

/**
 * Confirms retiring or restoring a sig, and says what moves with it.
 *
 * Two different things happen and only one of them is obvious. The directions cascade — an explicit
 * switch-off retires every direction on the sig. The drug approvals do NOT: `catalog_approved_sigs`
 * is left exactly as it is, so a retired sig keeps its approvals and restoring one brings them all
 * back unreviewed. Whether that should cascade too is an open product decision; until it is decided
 * the screen says out loud what today's behaviour is, because the alternative is a pharmacist
 * discovering it from a stuck order.
 */
const ConfirmSigStatusChangeDialog = ({
  sig,
  isActive,
  directionCount,
  approvedDrugCount,
  loading,
  onCancel,
  onConfirm,
}) => {
  const hasDirections = directionCount > 0;
  const hasApprovals = approvedDrugCount > 0;

  return (
    <Dialog open fullWidth maxWidth='xs' onClose={onCancel}>
      <DialogTitle>{isActive ? 'Deactivate this sig?' : 'Activate this sig?'}</DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ wordBreak: 'break-word', mb: 2 }}>{sig}</DialogContentText>

        {hasDirections && (
          <DialogContentText variant='body2' sx={{ mb: 1 }}>
            {isActive
              ? `${countPhrase(directionCount, 'direction', 'directions')} on this sig will be retired with it.`
              : `${countPhrase(directionCount, 'direction', 'directions')} on this sig will be restored with it.`}
          </DialogContentText>
        )}

        {hasApprovals && (
          <Alert severity='warning' sx={{ mt: 1 }}>
            {isActive
              ? `${countPhrase(approvedDrugCount, 'drug approval', 'drug approvals')} ${SigStatusChangeCopy.APPROVALS_STAY_ON_RETIRE}`
              : `${countPhrase(approvedDrugCount, 'drug approval', 'drug approvals')} ${SigStatusChangeCopy.APPROVALS_RETURN_ON_RESTORE}`}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={onCancel}>
          Cancel
        </Button>
        <LoadingButton
          variant='contained'
          color={isActive ? 'error' : 'primary'}
          loading={loading}
          onClick={onConfirm}
        >
          {isActive ? 'Deactivate' : 'Activate'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmSigStatusChangeDialog;

ConfirmSigStatusChangeDialog.propTypes = {
  sig: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  directionCount: PropTypes.number,
  approvedDrugCount: PropTypes.number,
  loading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ConfirmSigStatusChangeDialog.defaultProps = {
  isActive: false,
  directionCount: 0,
  approvedDrugCount: 0,
  loading: false,
};
